import { useState, useEffect, useRef } from 'react';
import {
    Search, BrickWallFire, Check, Trash2, Columns3, RotateCcw, CirclePlus,
    Wifi, Copy, Eye, EyeOff, Download, X, Server, Monitor, Cpu, Building,
    ChevronUp,
    ChevronDown,
    Filter,
    Network,
    Video, Pencil
} from 'lucide-react';

import { Modal } from "@mui/material";

import { useNavigate } from 'react-router-dom';
import ScrollContainer from 'react-indiana-drag-scroll';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { jwt_decode } from '../../utils/jwt_decode';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
// import { hasPermission } from '../utils/permissionsHelper';


// -- Types --
type ColumnKey =
    | 'host_name'
    | 'ip'
    | 'platform'
    | 'manufacturer'
    | 'os_build'
    | 'initial_scan_pending'
    | 'updated';

interface CopyButtonProps {
    onClick: () => void;
    copied: boolean;
    children: React.ReactNode;
}

const IpDisplay: React.FC<{ ip: string; maxLength?: number }> = ({
    ip,
    maxLength = 15
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const shouldTruncate = ip.length > maxLength;
    const displayIp = shouldTruncate && !isExpanded
        ? `${ip.substring(0, maxLength)}...`
        : ip;

    return (
        <div className="flex items-center gap-2">
            <span className="text-blue-500 font-medium font-mono">
                {displayIp}
            </span>
            {shouldTruncate && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className="text-blue-400 hover:text-blue-300 text-sm underline"
                >
                    {isExpanded ? <CirclePlus className='w-5 h-5 text-gray-400' /> : <CirclePlus className='w-5 h-5 text-gray-400' />}
                </button>
            )}
        </div>
    );
};

const InternalAssetResultsTable = () => {
    // --- State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [showColumnSettings, setShowColumnSettings] = useState(false);
    const [assetData, setAssetData] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const decodedToken = jwt_decode();
    const [copiedStates, setCopiedStates] = useState<{
        install: boolean; reinstall: boolean; uninstall: boolean;
    }>({
        install: false,
        reinstall: false,
        uninstall: false
    });
    const [selectedOS, setSelectedOS] = useState<'windows' | 'linux'>('windows');
    const [showInstallModal, setShowInstallModal] = useState(false);

    const [selectedAssetType, setSelectedAssetType] = useState('all');
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const typeDropdownRef = useRef<HTMLDivElement>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const assetTypeOptions = [
        { id: 'all', label: 'All Assets', icon: Filter, color: 'text-gray-400' },
        { id: 'local_asset', label: 'Servers', icon: Server, color: 'text-blue-400' },
        { id: 'switch', label: 'Switch', icon: Network, color: 'text-cyan-400' },
        { id: 'camera', label: 'Camera', icon: Video, color: 'text-emerald-400' },
    ];

    const storedLevel = localStorage.getItem('level');
    const level = storedLevel ? JSON.parse(storedLevel) : null;
    const clientId = level?.client_id;
    const organizationId = decodedToken.organization_id;
    // const [deleteAsset, setDeleteAsset] = useState<any>({});
    // const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    // --- Pagination ---
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // --- Inline Editing ---
    const [editingId, setEditingId] = useState<string | null>(null);
    const [tempHostname, setTempHostname] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    // --- Sorting ---
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const isSortable = (key: string) => !['actions'].includes(key);




    // --- Column visibility ---
    const [columnVisibility, setColumnVisibility] = useState<Record<ColumnKey, boolean>>({
        host_name: true,
        ip: true,
        platform: true,
        manufacturer: true,
        os_build: true,
        initial_scan_pending: true,
        updated: true,
    });

    const dropdownRef = useRef<HTMLDivElement>(null);

    // --- Fetch Data ---
    const fetchData = async (skip: number = (currentPage - 1) * itemsPerPage, limit: number = itemsPerPage, orderBy: Record<string, 'asc' | 'desc'>[] = [{ updated: 'desc' }]) => {
        try {
            setLoading(true);
            // example: sort by, skip, limit
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];

            const queryObj: any = {};
            if (clientId) queryObj.client_id = clientId;

            if (selectedAssetType !== 'all') {
                queryObj.asset_type = selectedAssetType;
            }

            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { hostname: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { ip_address: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { platform: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { manufacturer: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { os_build: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { os_version: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params = {
                query: JSON.stringify(queryObj),
                skip, limit,
                sort_by, sort_order
            };
            try {
                const response = await baseapi.get('/assets', { params });
                if (response.data.status) {
                    const rows = (response.data.data ?? []).map((row: any) => ({
                        id: row.id ?? row.agent_id,
                        name: row.hostname ?? row.name ?? '-',
                        host_name: row.hostname ?? '-',
                        ip: row.ip_address ?? '-',
                        platform: row.platform ?? '-',
                        manufacturer: row.manufacturer ?? '-',
                        os_build: row.os_build ?? row.os_version ?? '-',
                        client_id: row.client_id,
                        agent_id: row.agent_id,
                        initial_scan_pending: row.initial_scan_pending ?? false,
                        updated: row.updated ?? '-',
                        agent_type: row.agent_type,
                        organization_id: row.organization_id,
                        asset_type: row.asset_type
                    }));
                    setAssetData(rows);
                    setLoading(false);
                    setTotalPages(Math.ceil(response.data.total / limit));
                    setTotalItems(response.data.total);
                } else {
                    setAssetData([]);
                    setTotalPages(0);
                    setTotalItems(0);
                    setLoading(false);
                }
            } catch (apiError) {
                setAssetData([]);
                setTotalPages(0);
                setTotalItems(0);
                setLoading(false);
            }
        } catch (error: any) {
            showError(error.message);
            setAssetData([]);
            setTotalPages(0);
            setTotalItems(0);
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData(0, itemsPerPage);
    }, [selectedAssetType, debouncedSearchTerm]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowColumnSettings(false);
            }
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
                setShowTypeDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, []);

    const handleRefresh = () => { fetchData(); };


    const toggleColumn = (columnName: ColumnKey) => {
        setColumnVisibility(prev => ({
            ...prev,
            [columnName]: !prev[columnName]
        }));
    };
    const handleCopy = async (text: string, type: 'install' | 'reinstall' | 'uninstall'): Promise<void> => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [type]: true }));
            showSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} command copied!`);
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [type]: false }));
            }, 1500);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    // --- CopyButton ---
    const CopyButton: React.FC<CopyButtonProps> = ({ onClick, copied, children }) => (
        <button
            onClick={onClick}
            className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 transition-all duration-200"
        >
            <div className="relative w-3 h-3">
                <Copy className={`w-3 h-3 absolute transition-all duration-300 ${copied ? 'opacity-0 scale-75' : 'opacity-100 scale-100'}`} />
                <Check className={`w-3 h-3 absolute transition-all duration-300 text-green-400 ${copied ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} />
            </div>
            {children}
        </button>
    );
    const toggleAllColumns = () => {
        const allVisible = Object.values(columnVisibility).every(visible => visible);
        const newVisibility: Record<ColumnKey, boolean> = Object.keys(columnVisibility).reduce((acc, key) => {
            acc[key as ColumnKey] = !allVisible;
            return acc;
        }, {} as Record<ColumnKey, boolean>);
        setColumnVisibility(newVisibility);
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    // --- Pagination ---
    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage)
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value)
    };

    // --- Delete ---
    // const handleDelete = async (asset_id: string) => {
    //     try {
    //         const response = await baseapi.delete(`/internal-asset/${asset_id}`);
    //         if (response.data.status) {
    //             showSuccess(response.data.message);
    //             fetchData();
    //         } else {
    //             showError(response.data.message);
    //         }
    //     } catch (error: any) {
    //         showError(error.message);
    //     }
    // };


    // const scanning = async (asset_id: string) => {
    //     try {
    //         const params = { modules: ['*'] }
    //         const response = await baseapi.post(`/internal-asset/${asset_id}/scan`, params)
    //         if (response.data.status) {
    //             showSuccess(response.data.message)
    //             fetchData();
    //         }
    //         else {
    //             showError(response.data.message)
    //             fetchData();
    //         }
    //     }
    //     catch (error: any) {
    //         showError(error.message)
    //         fetchData();
    //     }
    // }

    const columns: {
        key: ColumnKey;
        label: string;
        icon: React.ElementType;
        color: string;
    }[] = [
            { key: 'ip', label: 'IP Address', icon: Wifi, color: 'text-orange-400' },
            { key: 'host_name', label: 'Host Name', icon: Server, color: 'text-blue-400' },
            { key: 'platform', label: 'Platform', icon: Monitor, color: 'text-green-400' },
            { key: 'manufacturer', label: 'Manufacturer', icon: Building, color: 'text-purple-400' },
            { key: 'os_build', label: 'OS Build', icon: Cpu, color: 'text-yellow-400' },
            { key: 'initial_scan_pending', label: 'Scan Status', icon: Eye, color: 'text-cyan-400' },
            { key: 'updated', label: 'Last Updated', icon: RotateCcw, color: 'text-pink-400' },
            // { key: 'actions', label: 'Action', icon: TableOfContents, color: 'text-gray-300' }
        ];



    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "-") return "-";

        try {
            // Normalize: if no timezone info, assume UTC and add Z
            const normalized =
                /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString)
                    ? dateString
                    : `${dateString}Z`;

            const date = new Date(normalized);

            if (isNaN(date.getTime())) return dateString;

            return date.toLocaleString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                // no timeZone → use browser local, or set "Asia/Kolkata" if you want fixed IST
                // timeZone: "Asia/Kolkata",
            });
        } catch {
            return dateString;
        }
    };


    // Update the handleAssetClick function to include tooltip logic
    const handleAssetClick = (assetId: any, client_id: any, agent_id: any, agent_type: any, organization_id: any, assettype: any, hostname: string, path: string = '/internal_scan') => {

        sessionStorage.setItem('internal_ids', JSON.stringify({ 'asset_id': assetId, 'client_id': client_id, 'agent_id': agent_id, 'agent_type': agent_type, 'organization_id': organization_id, asset_type: assettype, host_name: hostname, path: path }))
        navigate("/internalscan/dashboard")
    }

    const handleUpdateHostname = async (id: string, currentVal: any) => {
        if (!tempHostname || tempHostname.trim() === currentVal) {
            setEditingId(null);
            return;
        }

        try {
            setIsUpdating(true);
            const response = await baseapi.put(`/assets/${id}`, {
                hostname: tempHostname.trim(),
                is_hostname_updated: true
            });

            if (response.data.status) {
                showSuccess("Hostname updated successfully");
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            } else {
                showError(response.data.message || "Failed to update hostname");
            }
        } catch (error: any) {
            showError(error.message || "An error occurred");
        } finally {
            setIsUpdating(false);
            setEditingId(null);
        }
    };


    return (
        <div className="min-h-screen bg-gray-900 p-6 pb-20">
            <div className="max-w-9xl mx-auto">
                {/* Header */}
                <div className="relative pb-2">
                    <div className="flex items-center justify-center relative">
                        <div className="flex flex-col lg:flex-row items-center font-bold text-4xl gap-3 text-center lg:text-left">
                            <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-2 rounded-lg">
                                <BrickWallFire className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <span className="text-gray-300">Asset Results</span>
                            </div>
                        </div>
                    </div>
                    <p className=" flex justify-center items-center py-2 text-gray-400 text-sm sm:text-base">
                        Monitor and track asset details across your internal network
                    </p>
                </div>

                {/* Search and Controls */}
                <div className="mb-6 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left Side Group */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 w-full">
                        {/* Search Input */}
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search assets..."
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Asset Type Filter Dropdown */}
                        <div className="relative" ref={typeDropdownRef}>
                            <button
                                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                                className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white hover:bg-gray-700 transition-all duration-200 min-w-[140px] justify-between shadow-lg"
                            >
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const selected = assetTypeOptions.find(opt => opt.id === selectedAssetType) || assetTypeOptions[0];
                                        const Icon = selected.icon;
                                        return <Icon className={`w-4 h-4 ${selected.color}`} />;
                                    })()}
                                    <span className="text-sm">
                                        {assetTypeOptions.find(opt => opt.id === selectedAssetType)?.label || 'All Assets'}
                                    </span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showTypeDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showTypeDropdown && (
                                <div className="absolute left-0 top-full mt-2 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 py-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="px-3 py-2 border-b border-gray-700 mb-1">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Filter by Type</span>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto custom-scroll">
                                        {assetTypeOptions.map((option) => {
                                            const Icon = option.icon;
                                            const isActive = selectedAssetType === option.id;
                                            return (
                                                <button
                                                    key={option.id}
                                                    onClick={() => {
                                                        setSelectedAssetType(option.id);
                                                        setShowTypeDropdown(false);
                                                        setCurrentPage(1);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors relative group
                                                        ${isActive ? 'bg-orange-500/10 text-orange-400' : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'}
                                                    `}
                                                >
                                                    <div className={`p-1.5 rounded-lg transition-colors ${isActive ? 'bg-orange-500/20' : 'bg-gray-900 group-hover:bg-gray-800'}`}>
                                                        <Icon className={`w-4 h-4 ${isActive ? 'text-orange-400' : option.color}`} />
                                                    </div>
                                                    <span className="font-medium">{option.label}</span>
                                                    {isActive && (
                                                        <div className="absolute right-4 w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.6)]"></div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Columns + Refresh in a row */}
                        <div className="flex items-center gap-3">
                            {/* Column Settings */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowColumnSettings(!showColumnSettings)}
                                    className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-200"
                                >
                                    <Columns3 className="w-4 h-4" />
                                    Columns
                                </button>
                                {/* Dropdown */}
                                {showColumnSettings && (
                                    <div className="absolute left-0 right-0 sm:left-auto sm:right-0 top-full mt-2 w-50 sm:w-50 mx-4 sm:mx-0 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 max-h-[300px] overflow-y-auto custom-scroll">
                                        <div className="p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-sm font-semibold text-white">Column Visibility</h3>
                                                <button
                                                    onClick={toggleAllColumns}
                                                    className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                                                >
                                                    {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                {columns.map(({ key, label, icon: Icon, color }) => (
                                                    <label key={key} className="flex items-center gap-3 cursor-pointer hover:bg-gray-700 p-2 rounded">
                                                        <input
                                                            type="checkbox"
                                                            checked={columnVisibility[key]}
                                                            onChange={() => toggleColumn(key)}
                                                            className="sr-only"
                                                        />
                                                        <div className="flex items-center gap-2 flex-1">
                                                            {columnVisibility[key] ? (
                                                                <Eye className="w-4 h-4 text-green-400" />
                                                            ) : (
                                                                <EyeOff className="w-4 h-4 text-gray-500" />
                                                            )}
                                                            <Icon className={`w-4 h-4 ${color}`} />
                                                            <span className={`text-sm ${columnVisibility[key] ? 'text-white' : 'text-gray-500'}`}>
                                                                {label}
                                                            </span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {level?.name === 'dummy' && (
                                <button
                                    onClick={() => setShowInstallModal(true)}
                                    className="flex items-center gap-2 rounded-lg bg-gray-800 hover:bg-gray-600 border border-gray-600 text-white px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Download className="w-4 h-4" />
                                    Install Agent
                                </button>
                            )}
                            {/* Refresh Button */}
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                className="rounded-lg bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        {/* Asset Count */}
                        <span className="text-md text-gray-400 pl-1 sm:pl-0">
                            Total Assets: <span className="text-orange-400 font-semibold">{totalItems}</span>
                        </span>
                    </div>

                </div>

                {/* Table */}
                <div className="bg-gray-800 rounded-t-xl border border-gray-700 overflow-hidden shadow-2xl">
                    <ScrollContainer
                        className="overflow-x-auto custom-scroll cursor-grab active:cursor-grabbing"
                        horizontal={true}
                        vertical={false}
                        hideScrollbars={false}
                    >
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-700/50 border-b border-gray-600">
                                    {columns.map(({ key, label, icon: Icon, color }) =>
                                        columnVisibility[key] ? (
                                            <th
                                                key={key}
                                                onClick={() => handleSort(key)}
                                                className={`py-4 px-2 font-semibold text-sm select-none whitespace-nowrap min-w-[100px] ${color} text-left cursor-pointer`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Icon className="w-4 h-4" />
                                                    {label}
                                                    {isSortable(key) && (
                                                        sortConfig?.key === key ? (
                                                            sortConfig.direction === 'asc' ? (
                                                                <ChevronUp size={14} />
                                                            ) : (
                                                                <ChevronDown size={14} />
                                                            )
                                                        ) : (
                                                            <ChevronDown size={14} className="opacity-40" />
                                                        )
                                                    )}
                                                </div>
                                            </th>

                                        ) : null
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {assetData.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan={Object.values(columnVisibility).filter(Boolean).length} className="text-center py-8 text-gray-400">
                                            No data found
                                        </td>
                                    </tr>
                                ) : loading ? (
                                    <tr>
                                        <td colSpan={Object.values(columnVisibility).filter(Boolean).length} className="px-6 py-4 text-center text-gray-400">
                                            <div className="flex items-center justify-center">
                                                <img src='/loading.gif' className="w-20 h-18 mr-2" />
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    assetData.map((item: any, index: any) => (
                                        <tr
                                            key={item.id}
                                            className={`border-b border-gray-700 hover:bg-gray-750 transition-colors duration-200 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/50'}`}
                                        >
                                            {columnVisibility.ip && (
                                                <td className="py-4 px-4">
                                                    <button
                                                        className={`flex items-center gap-3 ${!item.initial_scan_pending
                                                            ? 'cursor-not-allowed opacity-70'
                                                            : 'cursor-pointer hover:opacity-80'
                                                            }`}
                                                        onClick={() => {
                                                            handleAssetClick(item.id, item.client_id, item.agent_id, item.agent_type, item.organization_id, item.asset_type, item.host_name)
                                                        }}
                                                    >
                                                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                                        <IpDisplay ip={item.ip} maxLength={15} />
                                                    </button>
                                                </td>
                                            )}
                                            {columnVisibility.host_name && (
                                                <td className="py-4 px-4">
                                                    {editingId === item.id ? (
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                className="bg-gray-700 border border-orange-500 rounded px-2 py-1 text-xs text-white focus:outline-none w-32"
                                                                value={tempHostname}
                                                                onChange={(e) => setTempHostname(e.target.value)}
                                                                autoFocus
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleUpdateHostname(item.id, item.host_name);
                                                                    if (e.key === 'Escape') setEditingId(null);
                                                                }}
                                                            />
                                                            <button
                                                                onClick={() => handleUpdateHostname(item.id, item.host_name)}
                                                                disabled={isUpdating}
                                                                className="text-green-400 hover:text-green-300"
                                                            >
                                                                <Check className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingId(null)}
                                                                disabled={isUpdating}
                                                                className="text-red-400 hover:text-red-300"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group/hostname">
                                                            <span className="text-gray-300 font-medium">{item.host_name || '-'}</span>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingId(item.id);
                                                                    setTempHostname(item.host_name === '-' ? '' : item.host_name);
                                                                }}
                                                                className="opacity-0 group-hover/hostname:opacity-100 text-gray-400 hover:text-orange-400 transition-opacity"
                                                            >
                                                                <Pencil className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            )}

                                            {columnVisibility.platform && (
                                                <td className="py-4 px-4">
                                                    <span className="text-gray-300 font-medium">{item.platform || '-'}</span>
                                                </td>
                                            )}
                                            {columnVisibility.manufacturer && (
                                                <td className="py-4 px-4">
                                                    <span className="text-gray-300 font-medium">{item.manufacturer || '-'}</span>
                                                </td>
                                            )}
                                            {columnVisibility.os_build && (
                                                <td className="py-4 px-4">
                                                    <span className="text-gray-300 font-medium">{item.os_build || '-'}</span>
                                                </td>
                                            )}
                                            {columnVisibility.initial_scan_pending && (
                                                <td className="py-4 px-4">
                                                    <div className="flex items-center gap-2">
                                                        {!item.initial_scan_pending ? (
                                                            <>
                                                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                                                <span className="text-yellow-400 font-medium text-sm">Pending</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                                                <span className="text-green-400 font-medium text-sm">Completed</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            )}

                                            {columnVisibility.updated && (
                                                <td className="py-4 px-4">
                                                    <span className="text-gray-300 font-medium text-sm">
                                                        {formatDate(item.updated)}
                                                    </span>
                                                </td>
                                            )}
                                            {/* {columnVisibility.actions && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-gray-800 z-10">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => scanning(item.id)}
                              className="text-gray-400 hover:text-red-300 p-1 rounded-full hover:bg-gray-600 transition-colors flex items-center cursor-pointer"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => {
                                  setDeleteAsset(item);
                                  setOpen(true);
                                }}
                                className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-gray-600 transition-colors flex items-center cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )} */}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </ScrollContainer>
                </div>

                {/* Table Footer */}
                <div className="bg-gray-800 rounded-b-lg border-t border-gray-700 px-6 py-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Left Side: Entries control & range info */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-300">Show</label>
                                <div className="relative">
                                    <ItemsPerPageDropdown
                                        value={itemsPerPage}
                                        onChange={(val) => handleItemsPerPageChange(val)}
                                    />
                                </div>
                                <span className="text-sm text-gray-300">entries</span>
                            </div>
                            <div className="text-sm text-gray-300">
                                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                            </div>
                        </div>
                        {/* Pagination controls */}
                        <div className="flex flex-wrap justify-start sm:justify-end items-center gap-2">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-700 transition-colors"
                            >
                                <span>&laquo;</span>
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-700 transition-colors"
                            >
                                <span>&lsaquo;</span>
                            </button>
                            <div className="flex items-center px-3 py-1 bg-orange-600 p-2 border rounded-md">
                                <p className="text-white">{currentPage}</p>
                            </div>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-700 transition-colors"
                            >
                                <span>&rsaquo;</span>
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-700 transition-colors"
                            >
                                <span>&raquo;</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            {/* <Modal open={open} onClose={() => setOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
          w-[95%] sm:w-[500px] md:w-[550px] max-w-2xl bg-gray-900/95 backdrop-blur-xl 
          border border-red-400/20 shadow-2xl p-4 rounded-2xl text-white
          relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-400/40 to-transparent"></div>
                
                    <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center mr-2 text-red-400">
                            <Trash2 className='h-7 w-7'></Trash2>
                        </div>
                        <div className='flex items-center justify-center'>
                            <h2 className="text-2xl font-bold text-white">Confirm Delete</h2>
                        </div>
                    </div>
          
                    <div className="mb-4 bg-gray-800/30 p-2 rounded-lg border border-gray-700/50">
                        <p className="text-white/90 mb-3 text-lg">
                            Are you sure you want to delete the Asset <span className="font-semibold text-orange-400">{deleteAsset?.name}</span>?
                        </p>
                        <p className="text-orange-400/70 text-sm">
                            This will permanently remove the asset and all associated data.
                        </p>
                    </div>
              
                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => setOpen(false)}
                            className="px-6 py-3 border border-gray-600/50 text-gray-300 rounded-lg
                    hover:border-gray-500 hover:bg-gray-800/50 hover:text-white
                    transition-all duration-200 font-medium min-w-[100px]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                handleDelete(deleteAsset.id)
                                setOpen(false);
                            }}
                            className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold
                    hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/20
                    transition-all duration-200 min-w-[100px]"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </Modal> */}

            {/* Install Agent Modal */}
            <Modal open={showInstallModal} onClose={() => setShowInstallModal(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
      w-[90%] sm:w-[520px] max-w-lg bg-gray-900/95 backdrop-blur-xl 
      border border-blue-400/20 shadow-2xl p-4 rounded-xl text-white
      relative overflow-hidden">

                    {/* Subtle top border */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent"></div>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                            <Download className='h-6 w-6 text-blue-400 mr-2' />
                            <h2 className="text-xl font-bold text-white">Install Agent</h2>
                        </div>
                        <button
                            onClick={() => setShowInstallModal(false)}
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    {/* OS Selection Tabs */}
                    <div className="flex mb-4 bg-white/10 backdrop-blur-md rounded-lg p-1 border border-white/20">
                        <button
                            onClick={() => setSelectedOS('windows')}
                            className={`flex-1 mr-2 flex items-center justify-center gap-2 py-1 px-4 rounded-md transition-all  ${selectedOS === 'windows'
                                ? 'bg-purple-400/20 backdrop-blur-sm border border-purple-400/50 text-white shadow-lg'
                                : ''
                                }`}
                        >
                            <img src="/windows.svg" alt="Windows" className="w-6 h-6" />
                            Windows
                        </button>
                        <button
                            onClick={() => setSelectedOS('linux')}
                            className={`flex-1 flex items-center justify-center gap-2 py-1 px-4 rounded-md transition-all  ${selectedOS === 'linux'
                                ? 'bg-purple-400/20 backdrop-blur-sm border border-purple-400/50 text-white shadow-lg'
                                : ''
                                }`}
                        >
                            <img src="/linux.svg" alt="Linux" className="w-6 h-6" />
                            Linux
                        </button>
                    </div>
                    {/* Command Sections */}
                    <div className="space-y-4">
                        {/* Install Command */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-green-400 flex items-center">
                                    <CirclePlus className="w-4 h-4 mr-1" />
                                    Install
                                </span>
                                <CopyButton
                                    onClick={() => {
                                        const installCmd = selectedOS === 'windows'
                                            ? `OvulScanner.exe -i${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`
                                            : `./OvulScanner -i${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`;
                                        handleCopy(installCmd, 'install');
                                    }}
                                    copied={copiedStates.install}
                                >
                                    Copy
                                </CopyButton>
                            </div>
                            <code className="text-green-400 font-mono text-xs block bg-gray-800/50 p-2 rounded border border-gray-700 break-all">
                                {selectedOS === 'windows'
                                    ? `OvulScanner.exe -i${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`
                                    : `./OvulScanner -i${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`
                                }
                            </code>
                        </div>
                        {/* Reinstall Command */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-yellow-400 flex items-center">
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Reinstall
                                </span>
                                <CopyButton
                                    onClick={() => {
                                        const reinstallCmd = selectedOS === 'windows'
                                            ? `OvulScanner.exe --reinstall${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`
                                            : `./OvulScanner --reinstall${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`;
                                        handleCopy(reinstallCmd, 'reinstall');
                                    }}
                                    copied={copiedStates.reinstall}
                                >
                                    Copy
                                </CopyButton>
                            </div>
                            <code className="text-yellow-400 font-mono text-xs block bg-gray-800/50 p-2 rounded border border-gray-700 break-all">
                                {selectedOS === 'windows'
                                    ? `OvulScanner.exe --reinstall${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`
                                    : `./OvulScanner --reinstall${organizationId ? ` -o ${organizationId}` : ''}${clientId ? ` -c ${clientId}` : ''}`
                                }
                            </code>
                        </div>
                        {/* Uninstall Command */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-red-400 flex items-center">
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Uninstall
                                </span>
                                <CopyButton
                                    onClick={() => {
                                        const uninstallCmd = selectedOS === 'windows' ? 'OvulScanner.exe -u' : './OvulScanner -u';
                                        handleCopy(uninstallCmd, 'uninstall');
                                    }}
                                    copied={copiedStates.uninstall}
                                >
                                    Copy
                                </CopyButton>
                            </div>
                            <code className="text-red-400 font-mono text-xs block bg-gray-800/50 p-2 rounded border border-gray-700">
                                {selectedOS === 'windows' ? 'OvulScanner.exe -u' : './OvulScanner -u'}
                            </code>
                        </div>
                    </div>
                    {/* Close Button */}
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={() => setShowInstallModal(false)}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>


        </div>
    );
};

export default InternalAssetResultsTable;
